"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import { Shield, Building2, MapPin, ArrowRight, Network, Crown, Sparkles, Layers } from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";

const ENTITIES = [
  {
    id: "cryptomize",
    badge: "Origin Entity",
    badgeIcon: Shield,
    title: "CryptoMize",
    accent: "indigo",
    iconBg: "bg-gradient-depth",
    body: "The original Digital Conglomerate — envisioned as the evolutionary merger of the pioneering work of its founders, beginning in Information Privacy and evolving through intelligence, perception, governance, and political strategy into a full-spectrum strategic operations organization.",
    tags: ["Information Privacy", "Intelligence", "Perception", "Governance", "Political Strategy"],
    cta: { label: "About the Organization", href: "/about/" },
    stats: [
      { v: "3", l: "Continents", color: "text-indigo" },
      { v: "10+", l: "Years", color: "text-denim" },
      { v: "30+", l: "Countries Served", color: "text-cyan" },
      { v: "5", l: "Strategic Domains", color: "text-emerald" },
    ],
  },
  {
    id: "maximize",
    badge: "Evolution Entity",
    badgeIcon: Crown,
    title: "MaxiMize Infinium",
    accent: "emerald",
    iconBg: "bg-gradient-clarity",
    body: "The evolution — the world's first Digital Conglomerate for Strategic Sovereignty. 60+ integrated services across five interconnected domains: Perception, Privacy, Politics, Policing, and Policy. Sovereign Operations Architecture: a six-stage methodology refined over hundreds of engagements.",
    tags: ["60+ Services", "Penta P's", "6-Stage Methodology", "Sovereign-Grade", "Zero Dependencies"],
    cta: { label: "Explore the services", href: "/services/" },
    stats: [
      { v: "9", l: "Sectors", color: "text-emerald" },
      { v: "60+", l: "Services", color: "text-cyan" },
      { v: "5", l: "Penta P's", color: "text-lime" },
      { v: "6", l: "Methodology", color: "text-amber-gold" },
    ],
  },
];

const HQ_BADGES = [
  { icon: MapPin, label: "Headquarters", value: "New Delhi, India", gradient: "bg-gradient-premium", text: "text-amber-gold" },
  { icon: Network, label: "Active Operations", value: "18 countries · 3 continents", gradient: "bg-gradient-royal", text: "text-purple" },
];

const ACCENT: Record<string, { text: string; bg: string; border: string; pill: string; tag: string; glow: string; iconBorder: string }> = {
  indigo: {
    text: "text-indigo",
    bg: "glass-tinted-denim",
    border: "border-denim/30",
    pill: "bg-denim/15 border-denim/30 text-denim",
    tag: "bg-denim/10 text-denim border-denim/20",
    glow: "bg-denim/12",
    iconBorder: "ring-denim/30",
  },
  emerald: {
    text: "text-emerald",
    bg: "glass-tinted-emerald",
    border: "border-emerald/30",
    pill: "bg-emerald/15 border-emerald/30 text-emerald",
    tag: "bg-emerald/10 text-emerald border-emerald/20",
    glow: "bg-emerald/12",
    iconBorder: "ring-emerald/30",
  },
};

export function OrganizationSection() {
  return (
    <Section id="organization" ariaLabel="The Organization" background="default">
      <SectionHeader
        icon={Building2}
        overline="The Organization"
        overlineClassName="text-amber-gold"
        pillClassName="glass-tinted-amber-gold border-amber-gold/20"
        title={
          <>
            The Context She <span className="text-gradient-royal">Operates Within.</span>
          </>
        }
        subtitle="Two entities, one operational constant. CryptoMize is the origin; MaxiMize Infinium is the evolution. Dr. Jyoti Kush is the co-architect of both."
      />

      <div className="grid lg:grid-cols-2 gap-5 lg:gap-6">
        {ENTITIES.map((e, i) => {
          const a = ACCENT[e.accent];
          return (
            <motion.div
              key={e.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: i * 0.12, ease: [0, 0, 0.2, 1] }}
              className={`relative glass-strong rounded-2xl p-8 md:p-10 overflow-hidden group border ${a.border}`}
            >
              {/* Multi-color corner glows per entity (not orange) */}
              <div aria-hidden className={`absolute -top-24 ${e.id === "cryptomize" ? "-left-24" : "-right-24"} size-72 rounded-full ${a.glow} blur-3xl group-hover:scale-125 transition-transform duration-700`} />
              {e.id === "cryptomize" ? (
                <div aria-hidden className="absolute -bottom-20 -right-20 size-60 rounded-full bg-cyan/10 blur-3xl" />
              ) : (
                <div aria-hidden className="absolute -bottom-20 -left-20 size-60 rounded-full bg-amber-gold/10 blur-3xl" />
              )}

              <div className="relative">
                <div className="flex items-center gap-3 mb-5">
                  <div
                    className={`size-12 rounded-xl ${e.iconBg} flex items-center justify-center shadow-lg ring-2 ${a.iconBorder} group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}
                  >
                    <e.badgeIcon className="size-6 text-white" />
                  </div>
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${a.pill}`}>
                    <span className="text-overline">{e.badge}</span>
                  </div>
                </div>
                <h3 className="text-display-sm mb-4">{e.title}</h3>
                <p className="text-body-md text-muted-foreground leading-relaxed mb-6">{e.body}</p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {e.tags.map((t) => (
                    <span key={t} className={`text-caption px-2.5 py-1 rounded-md border ${a.tag}`}>
                      {t}
                    </span>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {e.stats.map((s) => (
                    <div
                      key={s.l}
                      className="rounded-xl bg-muted/40 p-4 border border-border/30 hover:border-current/30 transition-colors"
                    >
                      <div className={`text-display-sm ${s.color} font-bold`}>{s.v}</div>
                      <div className="text-caption text-muted-foreground mt-0.5">{s.l}</div>
                    </div>
                  ))}
                </div>
                <Link
                  href={e.cta.href}
                  className={`inline-flex items-center gap-2 text-body-sm font-semibold ${a.text} hover:gap-3 transition-all group/link`}
                >
                  {e.cta.label}
                  <ArrowRight className="size-4 group-hover/link:translate-x-1 transition-transform" />
                </Link>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* HQ strip — multi-color, not orange */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-30px" }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="mt-12 max-w-4xl mx-auto"
      >
        <div className="glass-card rounded-xl p-5 flex flex-wrap items-center justify-center gap-6 text-center md:text-left">
          {HQ_BADGES.map((b, i) => (
            <React.Fragment key={b.label}>
              {i > 0 && <div className="hidden md:block w-px h-10 bg-border" />}
              <div className="flex items-center gap-3">
                <div
                  className={`size-12 rounded-lg ${b.gradient} flex items-center justify-center ring-1 ring-border/40`}
                >
                  <b.icon className="size-6 text-white" />
                </div>
                <div>
                  <div className={`text-overline ${b.text}`}>{b.label}</div>
                  <div className="text-heading-sm">{b.value}</div>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </motion.div>

      {/* Trust spectrum — 6 distinct colors */}
      <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
        {[
          { cls: "text-indigo", label: "Origin" },
          { cls: "text-emerald", label: "Evolution" },
          { cls: "text-cyan", label: "Scale" },
          { cls: "text-denim", label: "Discretion" },
          { cls: "text-amber-gold", label: "Sovereignty" },
          { cls: "text-purple", label: "Intelligence" },
        ].map((s) => (
          <span
            key={s.label}
            className="glass rounded-full px-3 py-1.5 text-caption font-medium"
          >
            <span className={s.cls}>·</span>{" "}
            <span className="text-muted-foreground ml-1">{s.label}</span>
          </span>
        ))}
      </div>
    </Section>
  );
}
